import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { DashboardFiltersFacade } from '../../domain/+state/dashboard-filters.facade';
import { DashboardFacade } from '../../domain/+state/dashboard.facade';
import { DashboardFilters, DashboardScope, IsoDate } from '../../domain/model/dashboard-filters.model';
import { DashboardVmState } from '../../domain/model/dashboard-vm.model';
import {
  PartnerScorecard,
  PartnerScorecardDataVm,
  PartnerScorecardVm,
} from '../../domain/model/partner-scorecard.model';
import { companyToday } from '../../shared/dashboard-filters.helper';
import { clampToNow, errorVm, loadingVm } from '../../shared/dashboard-vm.helper';
import { toPartnerScorecardDataVm } from '../../shared/partner-scorecard.helper';

const LOADING_VM: PartnerScorecardVm = loadingVm<PartnerScorecardDataVm>();

/** What a request is keyed on: the scope, and the company day so the server's month to date rolls over. */
interface ScorecardSelection {
  scope: DashboardScope;
  today: IsoDate;
}

function sameScorecardSelection(previous: ScorecardSelection, current: ScorecardSelection): boolean {
  return previous.scope === current.scope && previous.today === current.today;
}

/**
 * Screen facade of the partner scorecard page: one request per distinct `(scope, company day)`, since the server
 * fixes the window. The period segments never fetch; the first clock tick past company midnight does, once.
 * Component-provided; lives as long as the screen.
 */
@Injectable()
export class PartnerScorecardFacade {
  /** loading → data | error per request. */
  readonly vm$: Observable<PartnerScorecardVm>;

  constructor(
    private readonly dashboardFacade: DashboardFacade,
    private readonly filtersFacade: DashboardFiltersFacade,
  ) {
    this.vm$ = combineLatest([this.filtersFacade.filters$, this.filtersFacade.now$]).pipe(
      map(
        ([filters, now]: [DashboardFilters, Date]): ScorecardSelection => ({
          scope: filters.scope,
          today: companyToday(now),
        }),
      ),
      distinctUntilChanged(sameScorecardSelection),
      switchMap((selection: ScorecardSelection) => this.load(selection.scope)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** Errors are caught here so one failed request never ends `vm$`; the next scope or day fetches again. */
  private load(scope: DashboardScope): Observable<PartnerScorecardVm> {
    // Built once per response: a new chart reference per clock tick would re-animate the canvas.
    const response$ = this.dashboardFacade.getPartnerScorecard$(scope).pipe(
      map((scorecard: PartnerScorecard) => ({
        asOfUtc: scorecard.asOfUtc,
        data: toPartnerScorecardDataVm(scorecard),
      })),
    );
    return combineLatest([response$, this.filtersFacade.now$]).pipe(
      map(
        ([response, now]): PartnerScorecardVm => ({
          state: DashboardVmState.DATA,
          asOfUtc: clampToNow(response.asOfUtc, now),
          errorMessage: null,
          data: response.data,
        }),
      ),
      startWith(LOADING_VM),
      catchError((error: unknown) => of(errorVm<PartnerScorecardDataVm>(error))),
    );
  }
}
